import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState } from 'react';
import { useGovernanceConfigTableColumns } from './columns';

type QueryParams = RouterInputs['mspr']['governanceConfig']['listGovernanceConfigs'];

export const Route = createFileRoute('/console/$orgId/configuration/compliance/mspr/governance-config')({
    component: Governance,
    staticData: {
        ui: {
            useParentTitle: true,
        },
    },
});

function Governance() {
    const orgId = useOrgId();
    const navigate = useNavigate();

    const [whereConditions] = useState<QueryParams['where']>({});
    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: 10,
    });

    const { data, isLoading, refetch } = trpc.mspr.governanceConfig.listGovernanceConfigs.useQuery({
        ...queryParams,
        where: whereConditions,
    });

    const columns = useGovernanceConfigTableColumns();

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'governance-config-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        showSorterTooltip: false,
        emptyState: { noRecordsTitle: 'No governance checks yet' },
    });

    return (
        <PageController route={Route}>
            <Outlet />
            <TableToolbar
                title="Governance Configuration"
                actions={
                    <Button.Add.Link
                        label="Add new governance check"
                        to="/console/$orgId/configuration/compliance/mspr/governance-config/add"
                        params={{ orgId }}
                        variant="primary"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/compliance/mspr/governance-config/edit/$id',
                                params: { orgId, id },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
